// Upload Service for Metapic V2
// Handles file uploads to AWS Lambda backend

import { apiService } from './apiService';
import { AuthCore } from '../core/AuthCore';
// Removed backendAuthService import - using AuthCore instead
import { getMaxSizeForMimeType, isSupportedMimeType } from './fileTypes';

export interface UploadResponse {
  success: boolean;
  message: string;
  redirectUrl?: string;
  file?: {
    id: string;
    filename: string;
    originalName: string;
    size: number;
    mimeType: string;
    url: string;
    createdAt: string;
  };
}

export interface AlbumCreationResponse {
  success: boolean;
  message: string;
  redirectUrl?: string;
  albumId?: string;
  albumSlug?: string;
  processedAssets?: any[];
  partialSuccess?: boolean;
  requiresCredits?: boolean;
  errorCode?: string;
}

export interface FileItem {
  id: string;
  filename: string;
  originalName: string;
  size: number;
  mimeType: string;
  url: string;
  createdAt: string;
  updatedAt: string;
}

class UploadService {
  // Upload single file
  async uploadFile(file: File): Promise<UploadResponse> {
    // Get Supabase token from AuthCore
    const token = await AuthCore.getInstance().getAccessToken();

    if (!token) {
      throw new Error('Authentication required');
    }

    try {
      const response = await apiService.uploadFile(file, token);

      // Transform API response to UploadResponse format
      return {
        success: true,
        message: 'File uploaded successfully',
        file: {
          id: response.data.uploadId,
          filename: file.name,
          originalName: file.name,
          size: file.size,
          mimeType: file.type,
          url: '', // URL will be available after processing
          createdAt: response.data.confirmedAt
        }
      };
    } catch (error) {
      console.error('File upload failed:', error);
      throw error;
    }
  }

  // Upload multiple files
  async uploadFiles(files: File[]): Promise<UploadResponse[]> {
    const uploadPromises = files.map(file => this.uploadFile(file));

    try {
      const results = await Promise.allSettled(uploadPromises);

      return results.map((result, index) => {
        if (result.status === 'fulfilled') {
          return result.value;
        } else {
          console.error(`Upload failed for file ${files[index].name}:`, result.reason);
          return {
            success: false,
            message: `${files[index].name} is invalid, please remove and try again`,
          };
        }
      });
    } catch (error) {
      console.error('Batch upload failed:', error);
      throw error;
    }
  }

  /**
    * PHASE 1.5: File Validation & Album Creation (NOT AI Metadata Generation)
    *
    * IMPORTANT: This method is NOT the actual AI metadata generation!
    * AI metadata generation will be implemented in future phases.
    *
    * This method only:
    * 1. Validates uploaded files
    * 2. Checks and reserves user credits
    * 3. Creates album entry in database
    * 4. Returns redirect URL to /result page
    *
    * The actual AI processing (title, description, keywords generation)
    * happens later in Phase 3 after files are uploaded to S3.
    */
  async createAlbum(request: {
    validFiles: Array<{
      id: string;
      file: File;
      size: string;
    }>;
    albumId?: string;
    customInstructions?: string;
    settings?: {
      maxTitleLength?: number;
      maxDescriptionLength?: number;
      maxKeywords?: number;
    };
  }): Promise<AlbumCreationResponse> {
    // Check AuthCore state
    const authState = AuthCore.getInstance().getState();
    
    // If not authenticated, throw error immediately
    if (!authState.isAuthenticated || !authState.user) {
      console.error('❌ User not authenticated! Stopping album creation.');
      throw new Error('User must be logged in to create albums');
    }
    
    // Get Supabase access token from AuthCore
    const token = await AuthCore.getInstance().getAccessToken();
    

    if (!token) {
      // [SECURITY] Removed token logging;
      throw new Error('Authentication required - no valid token available');
    }

    try {
      // Convert validFiles to the format expected by apiService
      const apiRequest = {
        files: request.validFiles.map(vf => ({
          name: vf.file.name,
          size: vf.file.size,
          type: vf.file.type
        })),
        settings: {
          maxTitleLength: request.settings?.maxTitleLength ?? 60,
          maxDescriptionLength: request.settings?.maxDescriptionLength ?? 160,
          maxKeywords: request.settings?.maxKeywords ?? 10
        },
        customInstruction: request.customInstructions
      };




      const response = await apiService.createAlbum(apiRequest, token);

      return {
        success: true,
        message: 'Album creation completed successfully',
        redirectUrl: response.data.redirectUrl,
        albumId: response.data.albumId,
        albumSlug: response.data.albumSlug, // Use actual albumSlug from backend
        processedAssets: response.data.processedAssets
      };
    } catch (error: any) {
      console.error('❌ Album creation failed:', error);

      // Handle "Failed to reserve credits" as partial success
      if (error.message && error.message.includes('Failed to reserve credits')) {
        console.warn('⚠️ Backend reported "Failed to reserve credits" - treating as partial success');

        // Try to extract album data from error details if available
        const errorDetails = (error as any).details || {};
        const albumSlug = errorDetails.albumSlug || errorDetails.album_id;
        const albumId = errorDetails.albumId || errorDetails.album_id;

        // Return partial success with actual album data if available
        return {
          success: true,
          message: 'Album created but credit reservation failed - continuing with success flow',
          albumId: albumId || 'unknown-album',
          albumSlug: albumSlug || 'unknown-album',
          partialSuccess: true
        };
      }

      // For other errors, return failure
      return {
        success: false,
        message: error.message || 'Failed to create album'
      };
    }
  }

  // Get user files
  async getFiles(): Promise<FileItem[]> {
    // Get Supabase access token from AuthCore
    const token = await AuthCore.getInstance().getAccessToken();

    if (!token) {
      // Return empty array instead of throwing error for better UX
      // [SECURITY] Removed token logging;
      return [];
    }

    try {
      const response = await apiService.getAssets(token);
      return response.data.assets.map((file: any) => ({
        id: file.id,
        filename: file.fileName,
        originalName: file.fileName,
        size: file.fileSize,
        mimeType: file.fileType,
        url: file.fileUrl,
        createdAt: file.uploadedAt,
        updatedAt: file.processedAt || file.uploadedAt
      })) || [];
    } catch (error) {
      console.error('Failed to fetch files:', error);
      // Return empty array on API failure for better UX
      return [];
    }
  }

  // Delete file
  async deleteFile(fileId: string): Promise<{ success: boolean; message: string }> {
    // Get Supabase access token from AuthCore
    const token = await AuthCore.getInstance().getAccessToken();

    if (!token) {
      // Return failure response instead of throwing error for better UX
      // [SECURITY] Removed token logging;
      return { success: false, message: 'Authentication required to delete files' };
    }

    try {
      await apiService.deleteAsset(fileId, token);
      return { success: true, message: 'File deleted successfully' };
    } catch (error) {
      console.error('Failed to delete file:', error);
      // Return failure response on API failure for better UX
      return { success: false, message: 'Failed to delete file. Please try again.' };
    }
  }

  // Validate file before upload
  validateFile(file: File): { valid: boolean; error?: string } {
    if (!isSupportedMimeType(file.type)) {
      return {
        valid: false,
        error: `File type ${file.type} is not supported`
      };
    }

    const maxSize = getMaxSizeForMimeType(file.type);
    if (file.size > maxSize) {
      const maxSizeMB = Math.round(maxSize / (1024 * 1024));
      return {
        valid: false,
        error: `File size exceeds ${maxSizeMB}MB limit`
      };
    }

    return { valid: true };
  }

  /**
   * Phase 3 File Re-Validation (PV node in mermaid)
   * Validates individual file before S3 upload to prevent SessionStorage tampering
   */
  async validateFileBeforeUpload(file: File, options: {
    userId: string;
    sessionId: string;
    fileId: string;
  }): Promise<{
    success: boolean;
    security?: {
      mimeTypeValidated: boolean;
      fileSizeChecked: boolean;
      signatureVerified: boolean;
      contentScanned: boolean;
      svgSanitized: boolean;
      sessionStorageVerified: boolean;
    };
    warnings?: string[];
    errors?: string[];
    processingTime?: string;
  }> {
    // Get file signature (first 8 bytes as hex)
    const getFileSignature = (file: File): Promise<string> => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const arrayBuffer = reader.result as ArrayBuffer;
          const uint8Array = new Uint8Array(arrayBuffer.slice(0, 8));
          const hex = Array.from(uint8Array)
            .map(b => b.toString(16).padStart(2, '0'))
            .join('')
            .toUpperCase();
          resolve(hex);
        };
        reader.onerror = () => reject(new Error('Failed to read file signature'));
        reader.readAsArrayBuffer(file.slice(0, 8));
      });
    };

    // Get file data for SVG sanitization if needed
    const getSvgFileData = (file: File): Promise<string | undefined> => {
      return new Promise((resolve) => {
        if (file.type !== 'image/svg+xml') {
          resolve(undefined);
          return;
        }
        
        const reader = new FileReader();
        reader.onload = () => {
          const text = reader.result as string;
          // Convert to base64 for transmission
          resolve(btoa(text));
        };
        reader.onerror = () => resolve(undefined);
        reader.readAsText(file);
      });
    };

    try {
      const [fileSignature, fileData] = await Promise.all([
        getFileSignature(file),
        getSvgFileData(file)
      ]);

      const validationRequest = {
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type,
        fileSignature,
        fileData, // Only for SVG files
        userId: options.userId,
        sessionId: options.sessionId,
        fileId: options.fileId
      };

      // Get auth token
      const token = await AuthCore.getInstance().getAccessToken();
      if (!token) {
        throw new Error('Authentication required');
      }

      // Call Phase 3 validation endpoint
      const response = await apiService.validateFileBeforeUpload(validationRequest, token);

      // API returns ApiSuccessResponse, no need to check response.success
      return {
        success: true,
        security: response.data.security,
        warnings: response.data.warnings || [],
        processingTime: response.data.processingTime
      };
    } catch (error: any) {
      console.error('Phase 3 file validation failed:', error);
      return {
        success: false,
        errors: [error.message || 'File validation failed']
      };
    }
  }

  // Format file size for display
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
}

export const uploadService = new UploadService();
export default uploadService;